import { DateTime } from 'luxon';
import { getCurrentTimezone } from './timezoneUtils';

/**
 * Anchor date for titles / initialDate from a persisted range.
 * Prefers currentStart; for legacy entries without it, uses the midpoint of the
 * visible range so month views don't land on the prior-month grid spillover.
 */
export const resolveCalendarAnchorDate = (dateRange: {
  start: string;
  end: string;
  currentStart?: string;
}): DateTime => {
  if (dateRange.currentStart) {
    return DateTime.fromISO(dateRange.currentStart);
  }
  const start = DateTime.fromISO(dateRange.start);
  const end = DateTime.fromISO(dateRange.end);
  return start.plus({ milliseconds: end.diff(start).as('milliseconds') / 2 });
};

/**
 * Converts a date string to ISO8601 format
 * @param dateString - Date string in any format
 * @param timezone - Optional timezone (defaults to UTC)
 * @returns ISO8601 formatted string
 */
export const toISO8601 = (dateString: string, timezone = 'UTC'): string => {
  return DateTime.fromISO(dateString, { zone: timezone }).toISO();
};

/**
 * Extracts timezone from an ISO8601 string
 * @param isoString - ISO8601 formatted string
 * @returns Timezone string
 */
export const extractTimezone = (isoString: string): string => {
  const date = DateTime.fromISO(isoString);
  return date.zoneName || 'UTC';
};

/**
 * Formats a date string according to the specified format
 * @param dateString - Date string in any format
 * @param format - Format string (defaults to 'yyyy-MM-dd')
 * @param timezone - Optional timezone (defaults to UTC)
 * @returns Formatted date string
 */
export const formatDate = (
  dateString: string,
  format = 'yyyy-MM-dd',
  timezone = 'UTC',
): string => {
  return DateTime.fromISO(dateString, { zone: timezone }).toFormat(format);
};

/**
 * Formats a time string according to the specified format
 * @param dateString - Date string in any format
 * @param format - Format string (defaults to 'HH:mm')
 * @param timezone - Optional timezone (defaults to UTC)
 * @returns Formatted time string
 */
export const formatTime = (
  dateString: string,
  format = 'HH:mm',
  timezone = getCurrentTimezone(),
): string => {
  return DateTime.fromISO(dateString, { zone: timezone }).toFormat(format);
};

/**
 * Combines a date string and time string into a single ISO8601 string
 * @param dateString - Date string in any format
 * @param timeString - Time string in any format
 * @param timezone - Optional timezone (defaults to UTC)
 * @returns Combined ISO8601 string
 */
export const combineDateTime = (
  dateString: string,
  timeString: string,
  timezone = 'UTC',
): string => {
  const date = DateTime.fromISO(dateString, { zone: timezone });
  const time = DateTime.fromISO(timeString, { zone: timezone });

  return date
    .set({
      hour: time.hour,
      minute: time.minute,
      second: time.second,
    })
    .toISO();
};

/**
 * Validates if a string is a valid ISO8601 date
 * @param dateString - String to validate
 * @returns boolean indicating if the string is a valid ISO8601 date
 */
export const isValidISO8601 = (dateString: string): boolean => {
  return DateTime.fromISO(dateString).isValid;
};
